import { useEffect, useMemo, useState } from "react";
import type { PlatformClient } from "../../platform/supabase";

type Tab="TODAY"|"CONFIRMATIONS"|"CASES"|"TASKS"|"SETTINGS";
type QueueStatus="ACTION_REQUIRED"|"SCHOOL_CHECK_REQUIRED"|"REVIEWING"|"NO_IMPACT_UPDATE";

type QueueItem={
  queueItemId:string;ruleId?:string|null;candidateId?:string|null;confirmationRequestId?:string|null;
  status:QueueStatus;title:string;reason:string;sourceRefs:Array<Record<string,unknown>>;
  freshness:Record<string,unknown>;candidateStatus?:string|null;candidateVersion?:number|null;
  severity?:string|null;ruleText?:string|null;
};
type Confirmation={
  requestId:string;ruleId:string;question:string;context?:string|null;status:string;
  answerDecision?:string|null;answerText?:string|null;requestedAt:string;answeredAt?:string|null;
  version:number;requestedByName:string;ruleTitle:string;
};
type CareerCase={
  caseId:string;learnerId:string;displayName:string;caseType:string;title:string;summary?:string|null;
  priority:string;status:string;ownerPersonId:string;version:number;updatedAt:string;taskCount:number;
};
type CareerTask={
  taskId:string;title:string;taskType:string;priority:string;status:string;dueAt?:string|null;
  assigneePersonId:string;learnerId?:string|null;displayName?:string|null;caseId?:string|null;
  caseTitle?:string|null;sourceCandidateId?:string|null;sourceRuleId?:string|null;version:number;
  linkedEvidenceCount:number;
};
type Learner={learnerId:string;displayName:string;gradeLevel?:string|null;classLabel?:string|null};
type Staff={personId:string;displayName:string};
type Workspace={
  context:{
    academicYearId:string;yearCode:string;canManageSchool:boolean;
    schoolProfile?:{jurisdictionCode:string;districtCode?:string|null;schoolLevel:string;curriculumContext:Record<string,unknown>;version:number}|null;
    teacherProfile?:{experienceLevel:string;gradeScope:string[];assignmentScope:Record<string,unknown>;unknownFields:string[];version:number}|null;
    policyPack?:{packId:string;packKey:string;packVersion:number;contentHash:string;publishedAt:string;ruleCount:number}|null;
  };
  counts:{actionRequired:number;schoolCheck:number;reviewing:number;noImpact:number;openConfirmations:number;openCases:number;openTasks:number};
  queue:QueueItem[];confirmations:Confirmation[];cases:CareerCase[];tasks:CareerTask[];learners:Learner[];staff:Staff[];
};
type AssuranceAnswer={
  ruleId:string;title:string;ruleText:string;queueStatus:string;conclusion:string;misunderstanding:string;
  nextAction:string;sourceRefs:Array<{authorityName?:string;title?:string;canonicalUri?:string|null;sourceVersion?:number;contentHash?:string}>;reason:string;
};
type TaskDetail={
  task:{taskId:string;title:string;taskType:string;status:string;priority:string;dueAt?:string|null;learnerId?:string|null;caseId?:string|null;version:number;rationale?:string|null};
  linkedEvidence:Array<{evidenceId:string;title:string;status:string;evidenceType:string}>;
  eligibleEvidence:Array<{evidenceId:string;title:string;status:string;evidenceType:string}>;
};

function fmt(value?:string|null){
  if(!value)return "-";
  return new Date(value).toLocaleString("ko-KR",{year:"numeric",month:"2-digit",day:"2-digit",hour:"2-digit",minute:"2-digit"});
}
function queueLabel(value:QueueStatus){
  return value==="ACTION_REQUIRED"?"지금 조치":value==="SCHOOL_CHECK_REQUIRED"?"학교확인":value==="REVIEWING"?"검토중":"영향없음";
}
function statusLabel(value:string){
  const map:Record<string,string>={
    OPEN:"열림",ACTIVE:"진행",WAITING:"대기",RESOLVED:"해결",CLOSED:"종료",
    IN_PROGRESS:"진행 중",BLOCKED:"차단",COMPLETED:"완료",CANCELLED:"취소",
    ANSWERED:"답변 완료",CANDIDATE:"승인 대기",APPROVED:"승인"
  };
  return map[value] || value;
}
function parseCsv(value:string){return value.split(",").map((item)=>item.trim()).filter(Boolean);}

export function CenterAssurancePage({client,schoolId}:{client:PlatformClient;schoolId:string}){
  const [tab,setTab]=useState<Tab>("TODAY");
  const [data,setData]=useState<Workspace|null>(null);
  const [refresh,setRefresh]=useState(0);
  const [busy,setBusy]=useState("");
  const [error,setError]=useState("");
  const [message,setMessage]=useState("");
  const [answer,setAnswer]=useState<AssuranceAnswer|null>(null);
  const [confirmQuestion,setConfirmQuestion]=useState("");
  const [confirmContext,setConfirmContext]=useState("");
  const [answerText,setAnswerText]=useState<Record<string,string>>({});
  const [answerDecision,setAnswerDecision]=useState<Record<string,string>>({});
  const [caseLearner,setCaseLearner]=useState("");
  const [caseType,setCaseType]=useState("POLICY_SUPPORT");
  const [caseTitle,setCaseTitle]=useState("");
  const [caseSummary,setCaseSummary]=useState("");
  const [casePriority,setCasePriority]=useState("NORMAL");
  const [taskTitle,setTaskTitle]=useState("");
  const [taskPriority,setTaskPriority]=useState("NORMAL");
  const [taskLearner,setTaskLearner]=useState("");
  const [taskCase,setTaskCase]=useState("");
  const [taskAssignee,setTaskAssignee]=useState("");
  const [taskDue,setTaskDue]=useState("");
  const [taskRationale,setTaskRationale]=useState("");
  const [taskNote,setTaskNote]=useState<Record<string,string>>({});
  const [selectedTaskId,setSelectedTaskId]=useState("");
  const [taskDetail,setTaskDetail]=useState<TaskDetail|null>(null);
  const [linkCaseId,setLinkCaseId]=useState("");
  const [linkEvidenceId,setLinkEvidenceId]=useState("");
  const [experienceLevel,setExperienceLevel]=useState("UNKNOWN");
  const [gradeScope,setGradeScope]=useState("");
  const [assignmentRuleKeys,setAssignmentRuleKeys]=useState("");
  const [jurisdiction,setJurisdiction]=useState("KR");
  const [districtCode,setDistrictCode]=useState("");
  const [schoolLevel,setSchoolLevel]=useState("UNKNOWN");

  useEffect(()=>{
    let active=true;
    setError("");
    (async()=>{
      const refreshResult=await client.rpc("center_refresh_assurance_queue",{p_school_id:schoolId});
      if(!active)return;
      if(refreshResult.error){setError(refreshResult.error.message);return;}
      const workspace=await client.rpc("center_assurance_workspace",{p_school_id:schoolId});
      if(!active)return;
      if(workspace.error){setError(workspace.error.message);return;}
      const next=workspace.data as Workspace;
      setData(next);
      setCaseLearner((current)=>next.learners.some((item)=>item.learnerId===current)?current:next.learners[0]?.learnerId || "");
      setTaskLearner((current)=>next.learners.some((item)=>item.learnerId===current)?current:next.learners[0]?.learnerId || "");
      setTaskAssignee((current)=>next.staff.some((item)=>item.personId===current)?current:next.staff[0]?.personId || "");
      setTaskCase((current)=>next.cases.some((item)=>item.caseId===current)?current:"");
      const tp=next.context.teacherProfile;
      if(tp){
        setExperienceLevel(tp.experienceLevel);
        setGradeScope(tp.gradeScope.join(", "));
        const ruleKeys=Array.isArray(tp.assignmentScope?.ruleKeys) ? tp.assignmentScope.ruleKeys : [];
        setAssignmentRuleKeys((ruleKeys as string[]).join(", "));
      }
      const sp=next.context.schoolProfile;
      if(sp){
        setJurisdiction(sp.jurisdictionCode);
        setDistrictCode(sp.districtCode || "");
        setSchoolLevel(sp.schoolLevel);
      }
      setSelectedTaskId((current)=>next.tasks.some((item)=>item.taskId===current)?current:next.tasks[0]?.taskId || "");
    })();
    return()=>{active=false;};
  },[client,schoolId,refresh]);

  useEffect(()=>{
    if(!selectedTaskId){setTaskDetail(null);return;}
    let active=true;
    client.rpc("center_career_task_detail",{p_school_id:schoolId,p_task_id:selectedTaskId}).then(({data,error})=>{
      if(!active)return;
      if(error){setError(error.message);return;}
      const next=data as TaskDetail;
      setTaskDetail(next);
      setLinkCaseId(next.task.caseId || "");
      setLinkEvidenceId(next.eligibleEvidence[0]?.evidenceId || "");
    });
    return()=>{active=false;};
  },[client,schoolId,selectedTaskId,refresh]);

  const unknownFields=data?.context.teacherProfile?.unknownFields || [];
  const openConfirmations=useMemo(()=>data?.confirmations.filter((item)=>item.status==="OPEN") || [],[data]);
  const openCases=useMemo(()=>data?.cases.filter((item)=>!["RESOLVED","CLOSED"].includes(item.status)) || [],[data]);

  async function run(key:string,action:()=>PromiseLike<{data?:unknown;error?:{message:string}|null}>,success?:string){
    setBusy(key);setError("");setMessage("");
    try{
      const result=await action();
      if(result.error)throw new Error(result.error.message);
      if(success)setMessage(success);
      setRefresh((value)=>value+1);
      return result.data;
    }catch(err){
      setError(err instanceof Error?err.message:"처리 중 오류가 발생했습니다.");
      return null;
    }finally{setBusy("");}
  }

  async function showAnswer(ruleId:string){
    setBusy("answer");setError("");
    const {data,error}=await client.rpc("center_assurance_answer",{p_school_id:schoolId,p_rule_id:ruleId});
    setBusy("");
    if(error){setError(error.message);return;}
    setAnswer(data as AssuranceAnswer);
  }

  async function requestConfirmation(item:QueueItem){
    if(!item.ruleId || !confirmQuestion.trim())return;
    const result=await run("confirm-request",()=>client.rpc("center_request_assurance_confirmation",{
      p_school_id:schoolId,p_rule_id:item.ruleId,p_question:confirmQuestion.trim(),
      p_context:confirmContext.trim() || null,p_idempotency_key:crypto.randomUUID()
    }),"학교 확인 요청을 보냈습니다.");
    if(result){setConfirmQuestion("");setConfirmContext("");}
  }

  async function answerConfirmation(item:Confirmation){
    const decision=answerDecision[item.requestId] || "APPLICABLE";
    const text=answerText[item.requestId]?.trim() || "";
    if(!text)return;
    const result=await run("confirm-answer",()=>client.rpc("center_answer_assurance_confirmation",{
      p_school_id:schoolId,p_request_id:item.requestId,p_expected_version:item.version,
      p_decision_type:decision,p_answer_text:text
    }),"학교 결정이 확정됐습니다.");
    if(result)setAnswerText((current)=>({...current,[item.requestId]:""}));
  }

  async function approveCandidate(item:QueueItem){
    if(!item.candidateId || item.candidateVersion==null)return;
    await run("approve-candidate",()=>client.rpc("center_approve_assurance_task_candidate",{
      p_school_id:schoolId,p_candidate_id:item.candidateId,p_expected_version:item.candidateVersion,p_assignee_person_id:null
    }),"업무 후보를 승인해 Career ERP 업무로 전환했습니다.");
  }

  async function createCase(){
    if(!caseLearner || !caseTitle.trim())return;
    const result=await run("case-create",()=>client.rpc("center_create_student_career_case",{
      p_school_id:schoolId,p_learner_id:caseLearner,p_case_type:caseType,p_title:caseTitle.trim(),
      p_summary:caseSummary.trim() || null,p_priority:casePriority,p_source_rule_id:null
    }),"Student Career Case를 열었습니다.");
    if(result){setCaseTitle("");setCaseSummary("");}
  }

  async function transitionCase(item:CareerCase,toStatus:string){
    await run("case-"+item.caseId,()=>client.rpc("center_transition_student_career_case",{
      p_school_id:schoolId,p_case_id:item.caseId,p_expected_version:item.version,
      p_to_status:toStatus,p_note:"교사 업무안심에서 상태 변경"
    }));
  }

  async function createTask(){
    if(!taskTitle.trim())return;
    const caseItem=data?.cases.find((item)=>item.caseId===taskCase);
    const learnerId=caseItem?.learnerId || taskLearner || null;
    const result=await run("task-create",()=>client.rpc("center_create_career_task",{
      p_school_id:schoolId,p_title:taskTitle.trim(),p_task_type:"CAREER_SUPPORT",p_priority:taskPriority,
      p_due_at:taskDue?new Date(taskDue).toISOString():null,
      p_assignee_person_id:data?.context.canManageSchool ? (taskAssignee || null) : null,
      p_learner_id:learnerId,p_case_id:taskCase || null,p_program_id:null,p_source_rule_id:null,
      p_rationale:taskRationale.trim() || null,p_idempotency_key:crypto.randomUUID()
    }),"Career ERP 업무를 만들었습니다.");
    if(result){setTaskTitle("");setTaskRationale("");setTaskDue("");}
  }

  async function transitionTask(item:CareerTask,toStatus:string){
    const note=taskNote[item.taskId]?.trim() || "";
    if(toStatus==="COMPLETED" && !note)return;
    await run("task-"+item.taskId,()=>client.rpc("center_transition_career_task",{
      p_school_id:schoolId,p_task_id:item.taskId,p_expected_version:item.version,p_to_status:toStatus,p_note:note || null
    }));
  }

  async function linkCase(){
    if(!taskDetail || !linkCaseId)return;
    await run("link-case",()=>client.rpc("center_link_career_task_case",{
      p_school_id:schoolId,p_task_id:taskDetail.task.taskId,p_case_id:linkCaseId,p_expected_version:taskDetail.task.version
    }),"업무를 Student Career Case에 연결했습니다.");
  }

  async function linkEvidence(){
    if(!taskDetail || !linkEvidenceId)return;
    await run("link-evidence",()=>client.rpc("center_link_career_task_evidence",{
      p_school_id:schoolId,p_task_id:taskDetail.task.taskId,p_evidence_id:linkEvidenceId
    }),"학생 Evidence를 업무에 연결했습니다.");
  }

  async function saveTeacherProfile(){
    const keys=parseCsv(assignmentRuleKeys);
    const unknown:string[]=[];
    if(experienceLevel==="UNKNOWN")unknown.push("experience_level");
    if(parseCsv(gradeScope).length===0)unknown.push("grade_scope");
    if(keys.length===0)unknown.push("assignment_scope");
    await run("teacher-profile",()=>client.rpc("center_upsert_teacher_assurance_profile",{
      p_school_id:schoolId,p_experience_level:experienceLevel,p_grade_scope:parseCsv(gradeScope),
      p_assignment_scope:{ruleKeys:keys},p_unknown_fields:unknown
    }),"내 업무안심 Context를 저장했습니다.");
  }

  async function saveSchoolProfile(){
    await run("school-profile",()=>client.rpc("center_upsert_assurance_school_profile",{
      p_school_id:schoolId,p_jurisdiction_code:jurisdiction.trim(),p_school_level:schoolLevel,
      p_district_code:districtCode.trim() || null,p_curriculum_context:{}
    }),"학교 Assurance Context를 저장했습니다.");
  }

  if(error && !data)return <p className="error">{error}</p>;
  if(!data)return <p className="muted">업무안심을 준비하는 중…</p>;

  return (
    <section className="erp09-assurance-page">
      <header className="erp09-hero">
        <div>
          <p className="eyebrow">Teacher Assurance × Career ERP</p>
          <h2>업무안심</h2>
          <p>정책을 읽는 화면이 아니라, 오늘 확인할 것과 학생에게 이어질 실제 업무를 구분합니다.</p>
        </div>
        <div className="erp09-pack-chip">
          <span>{data.context.yearCode}학년도</span>
          <strong>{data.context.policyPack ? "Pack v"+data.context.policyPack.packVersion : "Pack 미연결"}</strong>
          <small>{data.context.policyPack ? data.context.policyPack.ruleCount+"개 검증 Rule" : "학교 Context 확인 필요"}</small>
        </div>
      </header>

      {error && <p className="error">{error}</p>}
      {message && <p className="teacher-success">{message}</p>}

      <div className="erp09-counts">
        <Count label="지금 조치" value={data.counts.actionRequired} emphasis />
        <Count label="학교확인" value={data.counts.schoolCheck} warning />
        <Count label="검토중" value={data.counts.reviewing} />
        <Count label="학생 Case" value={data.counts.openCases} />
        <Count label="진로업무" value={data.counts.openTasks} />
      </div>

      <nav className="erp09-tabs">
        {([
          ["TODAY","오늘의 업무안심"],["CONFIRMATIONS","학교확인"],["CASES","Student Career Case"],
          ["TASKS","진로업무"],["SETTINGS","적용정보"]
        ] as Array<[Tab,string]>).map(([value,label])=>(
          <button key={value} className={tab===value?"active":""} onClick={()=>setTab(value)}>{label}</button>
        ))}
      </nav>

      {tab==="TODAY" && (
        <div className="erp09-stack">
          <section className="erp09-principle">
            <strong>시스템이 임의로 업무를 만들지 않습니다.</strong>
            <span>검증된 근거 → 학교 Context → 학교 결정 → 업무분장 → 사람 승인 순서가 맞아야 실제 Task가 됩니다.</span>
          </section>
          {data.queue.map((item)=>(
            <article className={"erp09-queue-card "+item.status.toLowerCase()} key={item.queueItemId}>
              <div className="erp09-queue-status"><b>{queueLabel(item.status)}</b>{item.severity && <span>{item.severity}</span>}</div>
              <div className="erp09-queue-copy">
                <strong>{item.title}</strong><p>{item.reason}</p>{item.ruleText && <small>{item.ruleText}</small>}
              </div>
              <div className="erp09-queue-actions">
                {item.ruleId && <button className="secondary" disabled={busy!==""} onClick={()=>showAnswer(item.ruleId!)}>이거 맞나요?</button>}
                {item.status==="ACTION_REQUIRED" && item.candidateStatus==="CANDIDATE" && data.context.canManageSchool && (
                  <button className="primary" disabled={busy!==""} onClick={()=>approveCandidate(item)}>업무 승인</button>
                )}
              </div>
              {item.status==="SCHOOL_CHECK_REQUIRED" && item.ruleId && !item.confirmationRequestId && (
                <div className="erp09-confirm-compose">
                  <input value={confirmQuestion} onChange={(e)=>setConfirmQuestion(e.target.value)} placeholder="예: 우리 학교도 이 기준을 적용해야 하나요?" />
                  <input value={confirmContext} onChange={(e)=>setConfirmContext(e.target.value)} placeholder="학교 상황 메모 (선택)" />
                  <button className="primary" disabled={busy!=="" || !confirmQuestion.trim()} onClick={()=>requestConfirmation(item)}>학교확인 요청</button>
                </div>
              )}
            </article>
          ))}
          {!data.queue.length && <div className="assurance-empty"><strong>현재 확인할 정책 업무가 없습니다.</strong><p>새 Policy Pack이나 학교 Context 변화가 생기면 다시 계산됩니다.</p></div>}
        </div>
      )}

      {tab==="CONFIRMATIONS" && (
        <div className="erp09-stack">
          <div className="panel-title"><strong>학교확인 요청</strong><span>열림 {openConfirmations.length}건</span></div>
          {data.confirmations.map((item)=>(
            <article className="erp09-confirm-card" key={item.requestId}>
              <header><div><strong>{item.ruleTitle}</strong><span>{item.requestedByName} · {fmt(item.requestedAt)}</span></div><span className={item.status==="ANSWERED"?"status-pill good":"status-pill"}>{statusLabel(item.status)}</span></header>
              <p className="erp09-question">{item.question}</p>
              {item.context && <small>{item.context}</small>}
              {item.status==="ANSWERED" ? (
                <div className="erp09-answer-result"><b>{item.answerDecision}</b><span>{item.answerText}</span></div>
              ) : data.context.canManageSchool ? (
                <div className="erp09-answer-form">
                  <select value={answerDecision[item.requestId] || "APPLICABLE"} onChange={(e)=>setAnswerDecision((current)=>({...current,[item.requestId]:e.target.value}))}>
                    <option value="APPLICABLE">적용</option><option value="NOT_APPLICABLE">비적용</option>
                    <option value="DEFERRED">보류</option><option value="REQUEST_CLARIFICATION">추가확인</option>
                  </select>
                  <textarea rows={2} value={answerText[item.requestId] || ""} onChange={(e)=>setAnswerText((current)=>({...current,[item.requestId]:e.target.value}))} placeholder="판단 이유를 남겨야 확정할 수 있습니다." />
                  <button className="primary" disabled={busy!=="" || !(answerText[item.requestId] || "").trim()} onClick={()=>answerConfirmation(item)}>학교 결정 확정</button>
                </div>
              ) : <p className="muted">학교 관리자 답변을 기다리는 중입니다.</p>}
            </article>
          ))}
          {!data.confirmations.length && <p className="case-empty">학교확인 요청 이력이 없습니다.</p>}
        </div>
      )}

      {tab==="CASES" && (
        <div className="erp09-two">
          <section className="erp09-card">
            <div className="panel-title"><strong>Student Career Case 열기</strong><span>학생 중심 장기 업무</span></div>
            <label>학생<select value={caseLearner} onChange={(e)=>setCaseLearner(e.target.value)}>{data.learners.map((item)=><option key={item.learnerId} value={item.learnerId}>{item.displayName} · {item.gradeLevel || "-"}학년 {item.classLabel || "-"}반</option>)}</select></label>
            <div className="erp09-form-two">
              <label>Case 유형<select value={caseType} onChange={(e)=>setCaseType(e.target.value)}><option value="POLICY_SUPPORT">정책·지원</option><option value="PLANNING">진로설계</option><option value="TRANSITION">전환</option><option value="ASSESSMENT">검사</option><option value="EXPERIENCE">체험</option><option value="COUNSELING">상담</option><option value="OTHER">기타</option></select></label>
              <label>우선순위<select value={casePriority} onChange={(e)=>setCasePriority(e.target.value)}><option value="LOW">낮음</option><option value="NORMAL">일반</option><option value="HIGH">우선</option><option value="URGENT">긴급</option></select></label>
            </div>
            <label>Case 제목<input value={caseTitle} onChange={(e)=>setCaseTitle(e.target.value)} /></label>
            <label>상황 요약<textarea rows={3} value={caseSummary} onChange={(e)=>setCaseSummary(e.target.value)} /></label>
            <button className="primary" disabled={busy!=="" || !caseLearner || !caseTitle.trim()} onClick={createCase}>Case 열기</button>
          </section>
          <section className="erp09-card">
            <div className="panel-title"><strong>진로 Case</strong><span>{data.cases.length}건</span></div>
            <div className="erp09-case-list">
              {data.cases.map((item)=>(
                <article key={item.caseId}>
                  <header><div><strong>{item.displayName}</strong><span>{item.caseType} · 업무 {item.taskCount}건</span></div><span className="status-pill">{statusLabel(item.status)}</span></header>
                  <h4>{item.title}</h4>{item.summary && <p>{item.summary}</p>}
                  <div className="program-status-actions">
                    {item.status==="OPEN" && <button className="secondary" disabled={busy!==""} onClick={()=>transitionCase(item,"ACTIVE")}>진행 시작</button>}
                    {["OPEN","ACTIVE"].includes(item.status) && <button className="secondary" disabled={busy!==""} onClick={()=>transitionCase(item,"WAITING")}>대기</button>}
                    {!["RESOLVED","CLOSED"].includes(item.status) && <button className="primary compact-button" disabled={busy!==""} onClick={()=>transitionCase(item,"RESOLVED")}>해결</button>}
                    {item.status==="RESOLVED" && <button className="secondary" disabled={busy!==""} onClick={()=>transitionCase(item,"CLOSED")}>종료</button>}
                  </div>
                </article>
              ))}
              {!data.cases.length && <p className="case-empty">열린 Student Career Case가 없습니다.</p>}
            </div>
          </section>
        </div>
      )}

      {tab==="TASKS" && (
        <div className="erp09-two">
          <section className="erp09-card">
            <div className="panel-title"><strong>진로업무 만들기</strong><span>학생/Case 연결 선택</span></div>
            <label>업무명<input value={taskTitle} onChange={(e)=>setTaskTitle(e.target.value)} /></label>
            <div className="erp09-form-two">
              <label>학생<select value={taskLearner} onChange={(e)=>setTaskLearner(e.target.value)}><option value="">학생 연결 안 함</option>{data.learners.map((item)=><option key={item.learnerId} value={item.learnerId}>{item.displayName}</option>)}</select></label>
              <label>Student Case<select value={taskCase} onChange={(e)=>setTaskCase(e.target.value)}><option value="">Case 연결 안 함</option>{openCases.map((item)=><option key={item.caseId} value={item.caseId}>{item.displayName} · {item.title}</option>)}</select></label>
              <label>우선순위<select value={taskPriority} onChange={(e)=>setTaskPriority(e.target.value)}><option value="LOW">낮음</option><option value="NORMAL">일반</option><option value="HIGH">우선</option><option value="URGENT">긴급</option></select></label>
              <label>기한<input type="datetime-local" value={taskDue} onChange={(e)=>setTaskDue(e.target.value)} /></label>
              {data.context.canManageSchool && <label>담당자<select value={taskAssignee} onChange={(e)=>setTaskAssignee(e.target.value)}>{data.staff.map((item)=><option key={item.personId} value={item.personId}>{item.displayName}</option>)}</select></label>}
            </div>
            <label>필요 이유<textarea rows={3} value={taskRationale} onChange={(e)=>setTaskRationale(e.target.value)} /></label>
            <button className="primary" disabled={busy!=="" || !taskTitle.trim()} onClick={createTask}>진로업무 생성</button>
          </section>
          <section className="erp09-card">
            <div className="panel-title"><strong>진로업무</strong><span>{data.tasks.length}건</span></div>
            <div className="erp09-task-list">
              {data.tasks.map((item)=>(
                <article key={item.taskId} className={selectedTaskId===item.taskId?"selected":""} onClick={()=>setSelectedTaskId(item.taskId)}>
                  <header><div><strong>{item.title}</strong><span>{item.displayName || "학생 미연결"}{item.caseTitle?" · "+item.caseTitle:""}</span></div><span className={item.status==="COMPLETED"?"status-pill good":"status-pill"}>{statusLabel(item.status)}</span></header>
                  <small>{item.taskType} · {item.priority} · {item.dueAt?fmt(item.dueAt):"기한 없음"} · Evidence {item.linkedEvidenceCount}</small>
                  <div className="program-status-actions" onClick={(e)=>e.stopPropagation()}>
                    {item.status==="OPEN" && <button className="secondary" disabled={busy!==""} onClick={()=>transitionTask(item,"IN_PROGRESS")}>시작</button>}
                    {["OPEN","IN_PROGRESS"].includes(item.status) && <button className="secondary" disabled={busy!==""} onClick={()=>transitionTask(item,"BLOCKED")}>차단</button>}
                    {["OPEN","IN_PROGRESS","BLOCKED"].includes(item.status) && <><input value={taskNote[item.taskId] || ""} onChange={(e)=>setTaskNote((current)=>({...current,[item.taskId]:e.target.value}))} placeholder="완료 메모" /><button className="primary compact-button" disabled={busy!=="" || !(taskNote[item.taskId] || "").trim()} onClick={()=>transitionTask(item,"COMPLETED")}>완료</button></>}
                  </div>
                </article>
              ))}
            </div>
            {taskDetail && (
              <div className="erp09-task-linker">
                <strong>선택 업무 연결</strong>
                <div className="erp09-form-two">
                  <label>Student Case<select value={linkCaseId} onChange={(e)=>setLinkCaseId(e.target.value)}><option value="">선택</option>{openCases.filter((item)=>!taskDetail.task.learnerId || item.learnerId===taskDetail.task.learnerId).map((item)=><option key={item.caseId} value={item.caseId}>{item.displayName} · {item.title}</option>)}</select></label>
                  <button className="secondary" disabled={busy!=="" || !linkCaseId} onClick={linkCase}>Case 연결</button>
                  <label>학생 Evidence<select value={linkEvidenceId} onChange={(e)=>setLinkEvidenceId(e.target.value)}><option value="">선택</option>{taskDetail.eligibleEvidence.map((item)=><option key={item.evidenceId} value={item.evidenceId}>{item.title} · {item.status}</option>)}</select></label>
                  <button className="secondary" disabled={busy!=="" || !linkEvidenceId} onClick={linkEvidence}>Evidence 연결</button>
                </div>
                <p className="muted">정책 Source 자체는 Evidence로 연결할 수 없습니다. 같은 학생의 실제 Evidence만 연결됩니다.</p>
              </div>
            )}
          </section>
        </div>
      )}

      {tab==="SETTINGS" && (
        <div className="erp09-two">
          <section className="erp09-card">
            <div className="panel-title"><strong>내 적용정보</strong><span>UNKNOWN 허용</span></div>
            <label>경험수준<select value={experienceLevel} onChange={(e)=>setExperienceLevel(e.target.value)}><option value="UNKNOWN">아직 모름</option><option value="NEW">신규 담당</option><option value="EXPERIENCED">경험 있음</option><option value="LEAD">총괄 담당</option></select></label>
            <label>담당 학년<input value={gradeScope} onChange={(e)=>setGradeScope(e.target.value)} placeholder="예: 7, 8, 9" /></label>
            <label>확인된 Rule 업무분장 Key<input value={assignmentRuleKeys} onChange={(e)=>setAssignmentRuleKeys(e.target.value)} placeholder="확실할 때만 입력" /></label>
            {unknownFields.length>0 && <p className="muted">미확인: {unknownFields.join(", ")} — 비어 있어도 시스템이 추정해서 채우지 않습니다.</p>}
            <button className="primary" disabled={busy!==""} onClick={saveTeacherProfile}>내 적용정보 저장</button>
          </section>
          <section className="erp09-card">
            <div className="panel-title"><strong>학교 적용정보</strong><span>{data.context.canManageSchool?"학교 관리자":"읽기 전용"}</span></div>
            <label>관할 코드<input disabled={!data.context.canManageSchool} value={jurisdiction} onChange={(e)=>setJurisdiction(e.target.value)} /></label>
            <label>지역/교육지원청 코드<input disabled={!data.context.canManageSchool} value={districtCode} onChange={(e)=>setDistrictCode(e.target.value)} /></label>
            <label>학교급<select disabled={!data.context.canManageSchool} value={schoolLevel} onChange={(e)=>setSchoolLevel(e.target.value)}><option value="UNKNOWN">미확인</option><option value="ELEMENTARY">초등</option><option value="MIDDLE">중등</option><option value="HIGH">고등</option><option value="OTHER">기타</option></select></label>
            <div className="erp09-context-summary"><span>Policy Pack</span><strong>{data.context.policyPack?data.context.policyPack.packKey+" v"+data.context.policyPack.packVersion:"현재 관할·학년도 Pack 없음"}</strong></div>
            {data.context.canManageSchool && <button className="primary" disabled={busy!=="" || !jurisdiction.trim()} onClick={saveSchoolProfile}>학교 적용정보 저장</button>}
          </section>
        </div>
      )}

      {answer && (
        <aside className="erp09-answer-drawer">
          <button className="erp09-drawer-close" onClick={()=>setAnswer(null)}>닫기</button>
          <p className="eyebrow">이거 맞나요?</p><h3>{answer.title}</h3>
          <section><strong>결론</strong><p>{answer.conclusion}</p></section>
          <section><strong>오해하지 말 것</strong><p>{answer.misunderstanding}</p></section>
          <section><strong>다음 행동</strong><p>{answer.nextAction}</p></section>
          <section><strong>왜 이렇게 보이나요?</strong><p>{answer.reason}</p></section>
          <section><strong>근거</strong>{answer.sourceRefs.map((source,index)=><div className="erp09-source-ref" key={index}><b>{source.authorityName || "발행기관"}</b><span>{source.title || "근거자료"} · v{source.sourceVersion || "-"}</span>{source.canonicalUri && <a href={source.canonicalUri} target="_blank" rel="noreferrer">원문 열기</a>}</div>)}</section>
        </aside>
      )}
    </section>
  );
}

function Count({label,value,emphasis=false,warning=false}:{label:string;value:number;emphasis?:boolean;warning?:boolean}){
  return <div className={emphasis?"erp09-count emphasis":warning?"erp09-count warning":"erp09-count"}><span>{label}</span><strong>{value}</strong></div>;
}
